import { getApiGen } from "$root/api/factory";
import type { ReferenceItem } from "$root/pages/PortfoliosStudio/common";
import type { InvestmentItem } from "$root/pages/PortfoliosStudio/PortfolioList/const";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { sphereColumnsDef } from "$root/ui-lib/table/column/platform-columns-definition";
import type { AgGridColState } from "$root/ui-lib/table/col-state-schema";
import { adaptLegacyConfiguration } from "$root/widgets-architecture/layout/WidgetsGrid/legacy-adapter";
import { makeTaskQueue, parallelize, withRetry } from "$root/utils";
import { unreachable } from "@mdotm/utils/runtime";
import {
	type MarketViewListEntry,
	PageConfigurationsControllerApiFactory,
	PortfolioStudioPreferencesApiFactory,
	type ReferenceUniverseListEntry,
	SphereUiPreferencesMetadataControllerApiFactory,
	type UserMarketViewColumnPreference,
	type UserPortfolioColumnPreference,
	type UserReferenceColumnPreference,
	type UserUniverseColumnPreference,
	type UserInstrumentClassificationDto,
} from "@eu.mdotm.api/sphere";
import { noop } from "@mdotm/shadcn/legacy/utils";
import type { ColDef, ColumnState } from "ag-grid-community";
import { getCurrentUser } from ".";
import { registerPendingWrite } from "./pending-writes";
import { instrumentsRepository } from "../instruments/repository";
import env from "$root/env";

export const userStorage = makeRemoteStorage();
if (env.appEnv === "development") {
	(window as any).userStorage = userStorage;
}

/**
 * The queue above is where a preference write waits, and an identity change is what it must not wait
 * across — the header that attributes it is stamped as the request is sent. Registering the drain
 * centrally is what lets `settlePendingWrites` wait for writes nothing on screen is holding, such as
 * the column migrations below.
 */
registerPendingWrite({ drain: (signal) => userStorage.drain(signal) });
function makeRemoteStorage() {
	const updateQueue = makeTaskQueue();
	const storageBase = new window.Map();
	const listeners = new window.Map<string, Set<() => void>>();
	const api = () => getApiGen(SphereUiPreferencesMetadataControllerApiFactory);
	/**
	 * Keep trying the write until it lands, unless the queue has abandoned the task carrying it: past
	 * that point the identity that made the write is no longer the one a retry would be sent as.
	 */
	function send<T>(signal: AbortSignal, request: () => Promise<T>): Promise<T | undefined> {
		return withRetry(request, { maxRetries: 2, canRetry: () => !signal.aborted });
	}

	function notify(key: string) {
		const snapshot = listeners.get(key);
		if (snapshot) {
			for (const cb of [...snapshot]) {
				cb();
			}
		}
	}

	return {
		reset(newValues?: globalThis.Map<string, string> | Immutable.Map<string, string> | Record<string, string>) {
			// Every key that had a value before, or gets one now, must be notified so
			// mounted `useSyncExternalStore` subscribers re-read. Without this, resetting
			// (e.g. on session init or impersonate start/stop) leaves stale values from
			// the previous identity displayed in already-mounted components.
			const affectedKeys = new Set<string>(storageBase.keys());
			storageBase.clear();
			if (newValues) {
				for (const [key, val] of "entries" in newValues && typeof newValues.entries === "function"
					? newValues.entries()
					: Object.entries(newValues)) {
					storageBase.set(key, val);
					affectedKeys.add(key);
				}
			}
			for (const key of affectedKeys) {
				notify(key);
			}
		},
		set(key: string, value: string): void {
			storageBase.set(key, value);
			notify(key);
			updateQueue
				.enqueue((signal) =>
					send(signal, () => api().updateSphereUIPreferencesMetadataSpecificKeyForCurrentUser(key, value, { signal })),
				)
				.catch(noop);
		},
		get(key: string): string | null | undefined {
			return storageBase.get(key);
		},
		delete(key: string): void {
			storageBase.delete(key);
			notify(key);
			updateQueue
				.enqueue((signal) =>
					send(signal, () => api().deleteSphereUIPreferencesMetadataSpecificKeyForCurrentUser(key, { signal })),
				)
				.catch(noop);
		},
		clear(): void {
			const keys = [...storageBase.keys()];
			storageBase.clear();
			for (const key of keys) notify(key);
			updateQueue
				.enqueue((signal) => send(signal, () => api().deleteSphereUIPreferencesMetadataForCurrentUser({ signal })))
				.catch(noop);
		},
		getAll(): Record<string, string> {
			return Object.fromEntries(storageBase);
		},
		has(key: string): boolean {
			return storageBase.has(key);
		},
		subscribe(key: string, callback: () => void): () => void {
			if (!listeners.has(key)) {
				listeners.set(key, new Set());
			}
			listeners.get(key)!.add(callback);
			return () => {
				listeners.get(key)?.delete(callback);
			};
		},
		/**
		 * Resolve once every write handed to the queue before this call has gone out, or failed. The
		 * queue runs one task at a time in order, so waiting on a task appended at the end is waiting on
		 * all of them.
		 *
		 * Aborting `signal` gives all of them up: each write takes the signal the queue hands its task
		 * into its request config, so the one in flight is cancelled, the ones still queued never go out,
		 * and none of them retries.
		 */
		drain(signal?: AbortSignal): Promise<void> {
			return updateQueue.drain(signal);
		},
	};
}

export const deprecatedKeys: string[] = [];
function declare<K extends string>(key: K, opt?: { deprecated?: false }): Record<K, K>;
function declare<K extends string>(key: K, opt?: { deprecated: true }): object;
function declare<K extends string>(key: K, opt?: { deprecated?: boolean }): Record<K, K> | object;
function declare<K extends string>(key: K, opt?: { deprecated?: boolean }): Record<K, K> | object {
	if (opt?.deprecated) {
		deprecatedKeys.push(key);
		return {};
	}
	return { [key]: key } as Record<K, K>;
}

export const persistedColStateKeys = {
	...declare("table-instrument-customisation", { deprecated: true }),
	...declare("table-instrument-editor", { deprecated: true }),
	...declare("table-instrument-composition-review", { deprecated: true }),
	...declare("table-instrument-composition-read", { deprecated: true }),
	...declare("table-instrument-composition", { deprecated: true }),
	...declare("table-benchmarkComposition", { deprecated: true }),
	...declare("table-compositionWidget", { deprecated: true }),
	...declare("table-universeComposition", { deprecated: true }),
	...declare("tableV2-instrument-customisation", { deprecated: true }),
	...declare("tableV2-addStandardInstrument-read", { deprecated: true }),
	...declare("tableV2-addCustomInstrument-read", { deprecated: true }),
	...declare("tableV2-investment-composition-review", { deprecated: true }),
	...declare("tableV2-investment-composition-edit", { deprecated: true }),
	...declare("tableV2-investment-composition-read", { deprecated: true }),
	...declare("tableV2-investment-proposalComposition-read", { deprecated: true }),
	...declare("tableV2-investment-proposalComposition-edit", { deprecated: true }),
	...declare("tableV2-reference-composition-edit", { deprecated: true }),
	...declare("tableV2-reference-composition-read", { deprecated: true }),
	...declare("tableV2-reference-composition-review", { deprecated: true }),
	...declare("tableV2-universe-composition-edit", { deprecated: true }),
	...declare("tableV2-universe-composition-read", { deprecated: true }),
	...declare("tableV2-universe-composition-review", { deprecated: true }),
	...declare("tableV2-universe-compositionWithGroups-edit", { deprecated: true }),
	...declare("tableV2-universe-compositionWithGroups-read", { deprecated: true }),
	...declare("tableV3-investment-quantityComposition-edit", { deprecated: true }),
	...declare("tableV3-investment-quantityProposalComposition-edit", { deprecated: true }),
	...declare("tableV3-investment-quantityProposalComposition-read", { deprecated: true }),

	...declare("tableV3-instrument-customisation"),
	...declare("tableV3-addStandardInstrument-read"),
	...declare("tableV3-addCustomInstrument-read"),
	...declare("tableV3-investment-composition-review"),
	...declare("tableV3-investment-composition-edit"),
	...declare("tableV3-investment-composition-read"),
	...declare("tableV3-investment-proposalComposition-read"),
	...declare("tableV3-investment-proposalComposition-edit"),
	// V4 (MDOT-24700): the quantity tables' V3 state was saved while Quantity and Market price were
	// still unpinned, and a stored `pinned` always wins over the column definition on restore, so the
	// pinned-right default would never reach anyone who had opened those tables. The V3 keys above are
	// deprecated, which drops that state on next login.
	...declare("tableV4-investment-quantityComposition-edit"),
	...declare("tableV4-investment-quantityComposition-read"),
	// V5 (MDOT-25397): the V4 proposal state pins Weight and Delta right, which the proposal's
	// quantity comparison now holds; a stored `pinned` wins on restore, so V4 is deprecated too.
	...declare("tableV4-investment-quantityProposalComposition-edit", { deprecated: true }),
	...declare("tableV4-investment-quantityProposalComposition-read", { deprecated: true }),
	...declare("tableV5-investment-quantityProposalComposition-edit"),
	...declare("tableV5-investment-quantityProposalComposition-read"),
	...declare("tableV3-investment-quantityComposition-review"),
	...declare("tableV3-reference-composition-edit"),
	...declare("tableV3-reference-composition-read"),
	...declare("tableV3-reference-composition-review"),
	...declare("tableV3-universe-composition-edit"),
	...declare("tableV3-universe-composition-read"),
	...declare("tableV3-universe-composition-review"),
	...declare("tableV3-universe-compositionWithGroups-edit"),
	...declare("tableV3-universe-compositionWithGroups-read"),
	...declare("tableV3-marketViewList"),
	...declare("tableV3-universeList"),
	...declare("tableV3-portfolioList"),
	...declare("tableV3-referenceList"),
	...declare("instrument-investableUniverse-withStatus"),
	...declare("instrument-investableUniverse"),
} as const;

type PersistedColStateKeys = (typeof persistedColStateKeys)[keyof typeof persistedColStateKeys];

function syncColumnDefinition(key: PersistedColStateKeys) {
	switch (key) {
		case "tableV3-instrument-customisation":
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentCustomisation(classifications, "edit"),
			);
		case "tableV3-investment-composition-read":
		case "tableV3-reference-composition-read":
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentComposition(classifications, "read"),
			);
		case "tableV3-investment-composition-edit":
		case "tableV3-reference-composition-edit":
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentComposition(classifications, "edit"),
			);
		case "tableV3-investment-composition-review":
		case "tableV3-reference-composition-review":
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentReviewComposition(classifications, "edit"),
			);
		case "tableV3-universe-composition-review":
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentReviewComposition(classifications),
			);
		case "tableV3-universe-composition-edit":
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentUniverseComposition(classifications, "edit"),
			);
		case "tableV3-universe-composition-read":
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentUniverseComposition(classifications, "read"),
			);
		case "tableV3-universe-compositionWithGroups-edit":
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentComposition(classifications, "edit"),
			);
		case "tableV3-universe-compositionWithGroups-read":
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentComposition(classifications, "read"),
			);
		case "tableV3-investment-proposalComposition-edit":
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentProposalComposition(classifications, "edit"),
			);
		case "tableV4-investment-quantityComposition-edit":
			// currency is irrelevant for column-state syncing (only colIds/state are compared).
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentQuantityComposition(classifications, "edit"),
			);
		case "tableV4-investment-quantityComposition-read":
			// currency is irrelevant for column-state syncing (only colIds/state are compared).
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentQuantityComposition(classifications, "read"),
			);
		case "tableV5-investment-quantityProposalComposition-edit":
			// currency is irrelevant for column-state syncing (only colIds/state are compared).
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentQuantityProposalComposition(classifications, "edit"),
			);
		case "tableV3-investment-quantityComposition-review":
			// currency is irrelevant for column-state syncing (only colIds/state are compared).
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentQuantityReviewComposition(classifications, "edit"),
			);
		case "tableV5-investment-quantityProposalComposition-read":
			// currency is irrelevant for column-state syncing (only colIds/state are compared).
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentQuantityProposalComposition(classifications, "read"),
			);
		case "tableV3-investment-proposalComposition-read":
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentProposalComposition(classifications, "read"),
			);
		case "tableV3-addCustomInstrument-read":
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentCustomisation(classifications, "read"),
			);
		case "tableV3-addStandardInstrument-read":
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentStandardCustomisation(classifications),
			);
		case "tableV3-portfolioList":
			return columnWrapper("portfolio-studio", (preferences: UserPortfolioColumnPreference[]) => {
				const columns = columnsDefinition.portfolioList();
				const currentColumnPreference = preferences
					.map((preference) => {
						const matchedColumn: ColDef<InvestmentItem> | undefined = columns.find(
							(col) => col.colId === preference.preferencesType,
						);
						return matchedColumn ? { ...matchedColumn, hide: !preference.enabled } : undefined;
					})
					.filter((col) => col != null);
				return [...currentColumnPreference, sphereColumnsDef.colChooserAndCtxMenuTrigger()];
			});
		case "tableV3-referenceList":
			return columnWrapper("reference-studio", (preferences: UserReferenceColumnPreference[]) => {
				const columns = columnsDefinition.referenceList();
				const currentColumnPreference = preferences
					.map((preference) => {
						const matchedColumn: ColDef<ReferenceItem> | undefined = columns.find(
							(col) => col.colId === preference.preferencesType,
						);
						return matchedColumn ? { ...matchedColumn, hide: !preference.enabled } : undefined;
					})
					.filter((col) => col != null);
				return [...currentColumnPreference, sphereColumnsDef.colChooserAndCtxMenuTrigger()];
			});
		case "tableV3-universeList":
			return columnWrapper("universe-studio", (preferences: UserUniverseColumnPreference[]) => {
				const user = getCurrentUser();
				const columns = columnsDefinition.universeList(user);
				const currentColumnPreference = preferences
					.map((preference) => {
						const matchedColumn: ColDef<ReferenceUniverseListEntry> | undefined = columns.find(
							(col) => col.colId === preference.preferencesType,
						);
						return matchedColumn ? { ...matchedColumn, hide: !preference.enabled } : undefined;
					})
					.filter((col) => col != null);
				return [...currentColumnPreference, sphereColumnsDef.colChooserAndCtxMenuTrigger()];
			});
		case "tableV3-marketViewList":
			return columnWrapper("marketView-studio", (preferences: UserMarketViewColumnPreference[]) => {
				const columns = columnsDefinition.marketViewList();
				const currentColumnPreference = preferences
					.map((preference) => {
						const matchedColumn: ColDef<MarketViewListEntry> | undefined = columns.find(
							(col) => col.colId === preference.preferencesType,
						);
						return matchedColumn ? { ...matchedColumn, hide: !preference.enabled } : undefined;
					})
					.filter((col) => col != null);
				return [...currentColumnPreference, sphereColumnsDef.colChooserAndCtxMenuTrigger()];
			});
		case "instrument-investableUniverse":
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentProposalComposition(classifications, "read"),
			);
		case "instrument-investableUniverse-withStatus":
			return columnWrapper("instrument", (classifications: UserInstrumentClassificationDto[]) =>
				columnsDefinition.instrumentProposalComposition(classifications, "read"),
			);

		default:
			return unreachable(key);
	}
}

function columnWrapper<K extends string, T extends ((...args: any[]) => Array<ColDef<any>>) | Array<ColDef<any>>>(
	key: K,
	columns: T,
): { type: K; columns: T } {
	return { type: key, columns };
}

const instrumentColumnsDef = [
	persistedColStateKeys["tableV3-instrument-customisation"],
	persistedColStateKeys["tableV3-addStandardInstrument-read"],
	persistedColStateKeys["tableV3-addCustomInstrument-read"],
	persistedColStateKeys["tableV3-investment-composition-review"],
	persistedColStateKeys["tableV3-investment-composition-edit"],
	persistedColStateKeys["tableV3-investment-composition-read"],
	persistedColStateKeys["tableV3-investment-proposalComposition-read"],
	persistedColStateKeys["tableV3-investment-proposalComposition-edit"],
	persistedColStateKeys["tableV4-investment-quantityComposition-edit"],
	persistedColStateKeys["tableV4-investment-quantityComposition-read"],
	persistedColStateKeys["tableV5-investment-quantityProposalComposition-edit"],
	persistedColStateKeys["tableV5-investment-quantityProposalComposition-read"],
	persistedColStateKeys["tableV3-investment-quantityComposition-review"],
	persistedColStateKeys["tableV3-reference-composition-edit"],
	persistedColStateKeys["tableV3-reference-composition-read"],
	persistedColStateKeys["tableV3-reference-composition-review"],
	persistedColStateKeys["tableV3-universe-composition-edit"],
	persistedColStateKeys["tableV3-universe-composition-read"],
	persistedColStateKeys["tableV3-universe-composition-review"],
	persistedColStateKeys["tableV3-universe-compositionWithGroups-edit"],
	persistedColStateKeys["tableV3-universe-compositionWithGroups-read"],
];

const portfolioStudioDef = [
	persistedColStateKeys["tableV3-marketViewList"],
	persistedColStateKeys["tableV3-universeList"],
	persistedColStateKeys["tableV3-portfolioList"],
	persistedColStateKeys["tableV3-referenceList"],
];

const widgetGridKeys = ["widget-grid:PORTFOLIO_DETAILS", "widget-grid:PORTFOLIO_ENHANCEMENT"] as const;

const deprecatedWidgetGridKeys = [
	"widget-grid:PORTFOLIO_REFERENCE",
	"widget-grid:ASSET_CLASS",
	"widget-grid:DASHBOARD",
	"widget-grid:OUTLOOK",
	"widget-grid:HMM",
];

// One-off cleanup: the portfolio-lab performance column state + series visibility now live in the lab
// session view config (MDOT-24195), so these remote user-storage copies are no longer read or written.
const deprecatedLabKeys = ["lab-performance", "lab-performance-series-visibility"];

/** This identity's stored preferences, straight from the backend and with no side effect of its own. */
async function readRemoteUserPreferences(): Promise<Record<string, string>> {
	return await axiosExtract(
		getApiGen(SphereUiPreferencesMetadataControllerApiFactory).getSphereUIPreferencesMetadataForCurrentUser(),
	)
		.then((response) => response.data ?? {})
		.catch(() => ({}));
}

/** Read this identity's preferences into the store, then bring its legacy keys forward. */
export async function initUserStorage(): Promise<void> {
	userStorage.reset(await readRemoteUserPreferences());
	await migrateUserStorageColumns();
}

//refactor this, too much manual work need to be done to migrate a cols, impl was done in 1h for patch col migration
async function migrateUserStorageColumns(): Promise<void> {
	for (const key of deprecatedKeys) {
		if (userStorage.has(key)) {
			userStorage.delete(key);
		}
	}

	for (const key of deprecatedWidgetGridKeys) {
		if (userStorage.has(key)) {
			userStorage.delete(key);
		}
	}

	for (const key of deprecatedLabKeys) {
		if (userStorage.has(key)) {
			userStorage.delete(key);
		}
	}

	const instrumentColsToMigrate = instrumentColumnsDef.filter((key) => !userStorage.get(key));
	if (instrumentColsToMigrate.length > 0) {
		const instrumentPreferences = await axiosExtract(
			getApiGen(PortfolioStudioPreferencesApiFactory).getUserInstrumentsColumnPreferences(),
		);
		const userClassifications = await instrumentsRepository.allUserClassifications.fetchQuery();
		for (const key of instrumentColsToMigrate) {
			const columnToSync = syncColumnDefinition(key);
			if (columnToSync.type === "instrument") {
				userStorage.set(
					key,
					JSON.stringify({
						version: 1,
						data: columnToSync.columns(userClassifications ?? []).map((col) => ({
							colId: col.colId!,
							hide: !(
								instrumentPreferences.userInstrumentsColumnPreferences?.find(
									(x) => x.preferenceType?.classificationUuid === col.colId,
								)?.enabled ?? !col.hide
							),
							pinned: col.pinned,
							width: col.minWidth,
							rowGroup: col.rowGroup,
						})),
					} satisfies AgGridColState),
				);
			}
		}
	}

	const portfolioStudioColsToMigrate = portfolioStudioDef.filter((key) => !userStorage.get(key));
	if (portfolioStudioColsToMigrate.length > 0) {
		const portfolioListPreference = await axiosExtract(
			getApiGen(PortfolioStudioPreferencesApiFactory).getUserPortfolioColumnMetricsOrderingPreferences(),
		);
		const referenceListPreference = await axiosExtract(
			getApiGen(PortfolioStudioPreferencesApiFactory).getUserReferenceColumnOrderingPreferences(),
		);
		const universeListPreference = await axiosExtract(
			getApiGen(PortfolioStudioPreferencesApiFactory).getUserUniverseColumnMetricsOrderingPreferences(),
		);
		const marketViewListPreference = await axiosExtract(
			getApiGen(PortfolioStudioPreferencesApiFactory).getUserMarketViewColumnMetricsOrderingPreferences(),
		);

		for (const key of portfolioStudioColsToMigrate) {
			const columnToSync = syncColumnDefinition(key);
			if (columnToSync.type === "portfolio-studio") {
				userStorage.set(
					key,
					JSON.stringify({
						version: 1,
						data: columnToSync
							.columns(portfolioListPreference.userPortfolioColumnPreferences ?? [])
							.map(colDefToColumnState),
					} satisfies AgGridColState),
				);
			}

			if (columnToSync.type === "reference-studio") {
				userStorage.set(
					key,
					JSON.stringify({
						version: 1,
						data: columnToSync
							.columns(referenceListPreference.userReferenceColumnPreferences ?? [])
							.map(colDefToColumnState),
					} satisfies AgGridColState),
				);
			}

			if (columnToSync.type === "universe-studio") {
				userStorage.set(
					key,
					JSON.stringify({
						version: 1,
						data: columnToSync
							.columns(universeListPreference.userUniverseColumnPreferences ?? [])
							.map(colDefToColumnState),
					} satisfies AgGridColState),
				);
			}

			if (columnToSync.type === "marketView-studio") {
				userStorage.set(
					key,
					JSON.stringify({
						version: 1,
						data: columnToSync
							.columns(marketViewListPreference.userMarketViewColumnPreferences ?? [])
							.map(colDefToColumnState),
					} satisfies AgGridColState),
				);
			}
		}
	}

	const widgetGridsToMigrate = widgetGridKeys.filter((key) => !userStorage.has(key));
	if (widgetGridsToMigrate.length > 0) {
		const oldConfigs = await parallelize(
			widgetGridsToMigrate.map((key) => async () => {
				const gridName = key.replace("widget-grid:", "");
				const response = await getApiGen(PageConfigurationsControllerApiFactory)
					.pageWidgetsConfigurationsControllerGetPageConfiguration(gridName)
					.catch(() => null);
				return [key, response?.data ?? null] as const;
			}),
			{ concurrency: 5 },
		);
		for (const [key, oldConfig] of oldConfigs) {
			if (oldConfig?.configuration) {
				userStorage.set(
					key,
					JSON.stringify(await adaptLegacyConfiguration(oldConfig.configuration, key.replace("widget-grid:", ""))),
				);
			}
		}
	}
}

function colDefToColumnState<T>(col: ColDef<T>): ColumnState {
	return {
		colId: col.colId!,
		pinned: col.pinned,
		width: col.minWidth,
		rowGroup: col.rowGroup,
		hide: col.hide,
	};
}
